import React, { useMemo } from 'react'
import { Card, Divider } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Argument,
  ArgumentOption,
  ArgumentOptionConfig
} from '../../../pages/settings/company/keyFigures/formulas/types'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import ArgumentOptionList, {
  ApiConfig
} from '../../../pages/settings/company/keyFigures/formulas/components/ArgumentOptionList'
import { Context } from '../utils/contextAnalysis'

interface Props {
  addArgument: (argument: string) => void
  contextInfo: Context
}

const ContextAwareFunctionInformation: React.FC<Props> = ({ addArgument, contextInfo }) => {
  const { currentFunction, currentArgument, prevArgumentValue } = contextInfo
  const { t } = useTranslation()
  const globalCompanyId = useSelector(contextCompanyIdSelector)

  const effectiveCompanyId = contextInfo.company !== 'default' ? contextInfo.company : globalCompanyId

  const currentConfig = useMemo((): ArgumentOptionConfig | null => {
    if (!currentArgument) return null

    if (currentArgument.optionsConfig) {
      return currentArgument.optionsConfig
    }

    if (currentArgument.optionsByPrevArgument) {
      if (prevArgumentValue && currentArgument.optionsByPrevArgument[prevArgumentValue]) {
        return currentArgument.optionsByPrevArgument[prevArgumentValue]
      }
      if (currentArgument.optionsByPrevArgument.default) {
        return currentArgument.optionsByPrevArgument.default
      }
    }

    return null
  }, [currentArgument, prevArgumentValue])

  const selectorData = useSelector(state => {
    if (!currentConfig?.optionsSelector) return []
    if (typeof currentConfig.optionsSelector === 'function') {
      return currentConfig.optionsSelector(state)
    }
    return []
  })

  const staticOptions = useMemo(() => {
    const options: ArgumentOption[] = []
    if (!currentConfig) return options

    if (currentConfig.options) {
      options.push(...currentConfig.options)
    }

    if (selectorData) {
      options.push(...selectorData)
    }

    return options
  }, [currentConfig, selectorData])

  const apiConfig = useMemo((): ApiConfig | undefined => {
    if (!currentConfig) return undefined
    if (!effectiveCompanyId) return undefined
    return {
      config: currentConfig,
      companyId: effectiveCompanyId,
      prevArgumentValue
    }
  }, [currentConfig, effectiveCompanyId, prevArgumentValue])

  if (!currentFunction) {
    return null
  }

  return (
    <Card size="small">
      <Card.Meta
        title={`${currentFunction?.name}(${
          currentFunction?.arguments
            ? currentFunction.arguments?.map((arg: Argument) => `<${arg.label}>`).toString()
            : ''
        })`}
      />
      <Divider />
      <Card.Meta description={t(`formulaFunctionDescription:${currentFunction?.name}`)} />

      <Divider />
      <Card.Meta
        title={t('global:example')}
        style={{ marginBottom: 8 }}
        description={`${currentFunction?.name}(${currentFunction?.arguments
          ?.map((arg: Argument) => arg.example)
          .toString()})`}
      />
      <ArgumentOptionList options={staticOptions} apiConfig={apiConfig} height={400} addArgument={addArgument} />
    </Card>
  )
}

export default ContextAwareFunctionInformation
